import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  MenuItem,
  Modal,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  AdminPanelSettings,
  Close,
  DescriptionOutlined,
  History,
  Logout,
  MonitorHeartOutlined,
  OndemandVideo,
  PlayArrow,
  RemoveRedEye,
  Settings,
  Tune,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useStatsSettings } from '../context/Context';
import { useAuth } from '../context/AuthContext';
import { ApiModel, apiFetch, getApiErrorMessage, trackEvent } from '../config/api';
import { getModelCapabilities } from '../utils/modelCapabilities';
import { Capacitor } from '@capacitor/core';
import {
  getReminderSettings,
  requestPermission,
  scheduleReminder,
  cancelReminder,
} from '../utils/reminder';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const {
    selectedStats, toggleStat, selectedModel, updateSelectedModel,
    rhythmNetTransport, updateRhythmNetTransport,
    rhythmNetHopFrames, updateRhythmNetHopFrames,
    rhythmNetBufferWindows, updateRhythmNetBufferWindows,
    rhythmNetDenoiseEnabled, updateRhythmNetDenoiseEnabled,
  } = useStatsSettings();
  const { logout, isAdmin, username } = useAuth();
  const modelCapabilities = useMemo(() => getModelCapabilities(selectedModel), [selectedModel]);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [models, setModels] = useState<ApiModel[]>([]);
  const [isLoadingModels, setIsLoadingModels] = useState(true);
  const [modelsError, setModelsError] = useState('');
  const [gpuReachable, setGpuReachable] = useState<boolean | null>(null);
  // sv_selected_model persists across visits (Context.tsx), so a legacy
  // model (SeeVita v1/v2/v3 — PPG-only, see modelCapabilities.ts) picked
  // once during earlier testing stays "default" forever on that browser,
  // even after RhythmNet became the real, most-capable default. Upgrade
  // that stale choice to RhythmNet exactly once per page load — never
  // again after that, so it doesn't fight a deliberate manual re-selection
  // later in the same session.
  const hasResolvedInitialModel = useRef(false);
  const [showReminderBanner, setShowReminderBanner] = useState(() => {
    const last = localStorage.getItem('sv_last_scan_date');
    return last !== new Date().toISOString().slice(0, 10);
  });

  const [reminderEnabled, setReminderEnabled] = useState(() => getReminderSettings().enabled);
  const [reminderTime, setReminderTime] = useState(() => getReminderSettings().time);
  const [reminderError, setReminderError] = useState('');
  const isNative = Capacitor.isNativePlatform();

  const handleReminderToggle = async (enabled: boolean) => {
    setReminderError('');
    if (enabled) {
      const permission = await requestPermission();
      trackEvent('notification_permission_' + permission);
      if (permission !== 'granted') {
        setReminderError('Notification permission denied. Enable it in your device settings.');
        return;
      }
      await scheduleReminder(reminderTime);
      trackEvent('reminder_created', { time: reminderTime });
    } else {
      await cancelReminder();
      trackEvent('reminder_disabled');
    }
    setReminderEnabled(enabled);
  };

  const handleReminderTimeChange = async (time: string) => {
    setReminderTime(time);
    if (reminderEnabled) {
      await scheduleReminder(time);
      trackEvent('reminder_edited', { time });
    }
  };

  useEffect(() => {
    let isMounted = true;

    const fetchModels = async () => {
      setIsLoadingModels(true);
      setModelsError('');

      try {
        const response = await apiFetch('/api/models');

        if (!response.ok) {
          throw new Error(await getApiErrorMessage(response, 'Unable to load prediction models.'));
        }

        const data = (await response.json()) as { models?: ApiModel[]; gpuReachable?: boolean };
        const availableModels = data.models ?? [];

        if (!isMounted) return;

        setModels(availableModels);
        setGpuReachable(data.gpuReachable ?? null);
        const ids = availableModels.map((m) => m.id);
        const preferred = availableModels.find((m) => m.id.toLowerCase().includes('rhythmnet'))
          ?? availableModels.find((m) => m.id.toLowerCase().includes('deepphys'))
          ?? availableModels[0];
        const isStaleLegacySelection = !!selectedModel
          && ids.includes(selectedModel)
          && !selectedModel.toLowerCase().includes('rhythmnet')
          && preferred?.id.toLowerCase().includes('rhythmnet');
        if (!hasResolvedInitialModel.current && isStaleLegacySelection) {
          updateSelectedModel(preferred.id);
        } else if (selectedModel && !ids.includes(selectedModel)) {
          updateSelectedModel(preferred?.id ?? '');
        } else if (!selectedModel && availableModels.length > 0) {
          updateSelectedModel(preferred.id);
        }
        hasResolvedInitialModel.current = true;
      } catch (error) {
        if (!isMounted) return;
        setModelsError(error instanceof Error ? error.message : 'Unable to load prediction models.');
      } finally {
        if (isMounted) setIsLoadingModels(false);
      }
    };

    fetchModels();

    return () => {
      isMounted = false;
    };
  }, [selectedModel, updateSelectedModel]);

  const selectedStatsCount = useMemo(
    () => Object.values(selectedStats).filter(Boolean).length,
    [selectedStats],
  );

  const activeModelName = models.find((model) => model.id === selectedModel)?.name || 'Not selected';

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  // ── Simple view for regular users ──────────────────────────────────────────
  if (!isAdmin) {
    return (
      <Box sx={{
        minHeight: '100vh',
        background: 'linear-gradient(160deg, #0a1628 0%, #0d1f3c 58%, #102040 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        px: 3,
        py: 4,
      }}>
        {/* Header */}
        <Box sx={{ width: '100%', maxWidth: 480, display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
            <RemoveRedEye sx={{ fontSize: 15, color: '#60a5fa' }} />
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: 'white', letterSpacing: '4px', textTransform: 'uppercase' }}>
              SeeVita
            </Typography>
          </Box>
          <Button onClick={handleLogout} startIcon={<Logout sx={{ fontSize: '1.1rem' }} />}
            sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'none', fontSize: '0.9rem', fontWeight: 600,
              '&:hover': { color: '#fecaca', bgcolor: 'transparent' } }}>
            Sign out
          </Button>
        </Box>

        {/* Greeting */}
        <Box sx={{ width: '100%', maxWidth: 480, mb: 5, textAlign: 'center' }}>
          <Typography sx={{ fontSize: { xs: '2rem', sm: '2.4rem' }, fontWeight: 900, color: '#fff', lineHeight: 1.15, mb: 1.5 }}>
            {username ? `Good to see you, ${username.split(' ')[0]}.` : 'Good to see you.'}
          </Typography>
          <Typography sx={{ fontSize: '1.05rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1.6 }}>
            {showReminderBanner
              ? "You haven't scanned today — start below to keep your data fresh."
              : 'Your scan is up to date. Check your history or latest report below.'}
          </Typography>
        </Box>

        {/* Three big action tiles */}
        <Box sx={{ width: '100%', maxWidth: 480, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Scan — primary, largest */}
          <Box onClick={() => navigate('/monitor')}
            sx={{
              bgcolor: '#fff', borderRadius: '20px', p: { xs: 3, sm: 3.5 },
              display: 'flex', alignItems: 'center', gap: 2.5, cursor: 'pointer',
              boxShadow: '0 8px 32px rgba(0,0,0,0.28)',
              transition: 'transform 0.15s, box-shadow 0.15s',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 14px 40px rgba(0,0,0,0.36)' },
              '&:active': { transform: 'scale(0.98)' },
            }}>
            <Box sx={{ width: 56, height: 56, borderRadius: '14px', bgcolor: '#0a1628',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <MonitorHeartOutlined sx={{ color: '#60a5fa', fontSize: 28 }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: '1.2rem', fontWeight: 900, color: '#0a1628', lineHeight: 1.2 }}>
                Start a Scan
              </Typography>
              <Typography sx={{ fontSize: '0.88rem', color: '#64748b', mt: 0.4, lineHeight: 1.4 }}>
                Takes about 30 seconds
              </Typography>
            </Box>
            <PlayArrow sx={{ color: '#0a1628', fontSize: 24, ml: 'auto', opacity: 0.5 }} />
          </Box>

          {/* History */}
          <Box onClick={() => navigate('/history')}
            sx={{
              bgcolor: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: '20px', p: { xs: 3, sm: 3.5 },
              display: 'flex', alignItems: 'center', gap: 2.5, cursor: 'pointer',
              transition: 'transform 0.15s, background 0.15s',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.11)', transform: 'translateY(-2px)' },
              '&:active': { transform: 'scale(0.98)' },
            }}>
            <Box sx={{ width: 56, height: 56, borderRadius: '14px', bgcolor: 'rgba(96,165,250,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <History sx={{ color: '#60a5fa', fontSize: 28 }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: '1.2rem', fontWeight: 900, color: '#fff', lineHeight: 1.2 }}>
                My History
              </Typography>
              <Typography sx={{ fontSize: '0.88rem', color: 'rgba(255,255,255,0.45)', mt: 0.4, lineHeight: 1.4 }}>
                View past scans and trends
              </Typography>
            </Box>
            <PlayArrow sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 24, ml: 'auto' }} />
          </Box>

          {/* Report */}
          <Box onClick={() => navigate('/report')}
            sx={{
              bgcolor: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: '20px', p: { xs: 3, sm: 3.5 },
              display: 'flex', alignItems: 'center', gap: 2.5, cursor: 'pointer',
              transition: 'transform 0.15s, background 0.15s',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.11)', transform: 'translateY(-2px)' },
              '&:active': { transform: 'scale(0.98)' },
            }}>
            <Box sx={{ width: 56, height: 56, borderRadius: '14px', bgcolor: 'rgba(96,165,250,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <DescriptionOutlined sx={{ color: '#60a5fa', fontSize: 28 }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: '1.2rem', fontWeight: 900, color: '#fff', lineHeight: 1.2 }}>
                My Report
              </Typography>
              <Typography sx={{ fontSize: '0.88rem', color: 'rgba(255,255,255,0.45)', mt: 0.4, lineHeight: 1.4 }}>
                Latest vital sign summary
              </Typography>
            </Box>
            <PlayArrow sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 24, ml: 'auto' }} />
          </Box>
        </Box>
      </Box>
    );
  }

  // ── Admin dashboard ─────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(160deg, #0a1628 0%, #0d1f3c 58%, #102040 100%)',
        color: 'white',
        px: { xs: 2, md: 5 },
        py: { xs: 2, md: 4 },
      }}
    >
      <Box sx={{ maxWidth: 1180, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, letterSpacing: '4px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.85)' }}>
              SeeVita
            </Typography>
            <Typography sx={{ mt: 1, fontSize: { xs: '2rem', md: '3rem' }, fontWeight: 800, lineHeight: 1.05 }}>
              Vital monitoring dashboard
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton onClick={() => navigate('/admin')} sx={headerIconSx} aria-label="Admin">
              <AdminPanelSettings />
            </IconButton>
            <IconButton onClick={() => setIsSettingsOpen(true)} sx={headerIconSx} aria-label="Open settings">
              <Settings />
            </IconButton>
            <IconButton onClick={handleLogout} sx={{ ...headerIconSx, color: '#fecaca' }} aria-label="Sign out">
              <Logout />
            </IconButton>
          </Box>
        </Box>

        {modelsError && (
          <Alert severity="warning" sx={{ borderRadius: '10px' }}>
            {modelsError}
          </Alert>
        )}

        {showReminderBanner && (
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
            px: 2.5, py: 1.5, borderRadius: '14px',
            bgcolor: 'rgba(99,179,237,0.1)', border: '1px solid rgba(99,179,237,0.25)',
          }}>
            <Typography sx={{ fontSize: '0.88rem', color: 'rgba(255,255,255,0.75)' }}>
              You haven't scanned today — a quick session keeps your trend data fresh.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
              <Button size="small" variant="contained"
                onClick={() => { setShowReminderBanner(false); navigate('/monitor'); }}
                sx={{ borderRadius: '10px', bgcolor: '#63b3ed', color: '#0a1628', fontWeight: 800, textTransform: 'none', fontSize: '0.8rem', py: 0.6, px: 1.5, '&:hover': { bgcolor: '#90cdf4' } }}>
                Scan now
              </Button>
              <Button size="small" onClick={() => setShowReminderBanner(false)}
                sx={{ borderRadius: '10px', color: 'rgba(255,255,255,0.4)', textTransform: 'none', fontSize: '0.8rem', py: 0.6, px: 1, minWidth: 0, '&:hover': { color: 'rgba(255,255,255,0.7)', bgcolor: 'transparent' } }}>
                Dismiss
              </Button>
            </Box>
          </Box>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.2fr 0.8fr' }, gap: 2.5 }}>
          <Box sx={panelSx}>
            <Typography sx={{ color: 'rgba(255,255,255,0.52)', fontSize: '0.9rem', lineHeight: 1.7, maxWidth: 640 }}>
              Capture camera-based signals, run model inference, and review vital signs from one secure workspace.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 4 }}>
              <Button variant="contained" startIcon={<PlayArrow />} onClick={() => navigate('/monitor')}
                disabled={!selectedModel || isLoadingModels} sx={primaryButtonSx}>
                Start monitoring
              </Button>
              <Button variant="outlined" startIcon={<DescriptionOutlined />} onClick={() => navigate('/report')} sx={secondaryButtonSx}>
                Reports
              </Button>
              <Button variant="outlined" startIcon={<History />} onClick={() => navigate('/history')} sx={secondaryButtonSx}>
                History
              </Button>
              <Button variant="outlined" startIcon={<OndemandVideo />} onClick={() => navigate('/demo')} sx={secondaryButtonSx}>
                Video demo
              </Button>
            </Box>
          </Box>

          <Box sx={panelSx}>
            <Typography sx={panelLabelSx}>System Status</Typography>
            <Box sx={{ display: 'grid', gap: 1.25, mt: 2 }}>
              <StatusRow label="Auth" value="Secured" tone="success" />
              <StatusRow
                label="GPU server"
                value={isLoadingModels ? 'Checking' : gpuReachable == null ? 'Unknown' : gpuReachable ? 'Online' : 'Offline'}
                tone={gpuReachable ? 'success' : 'warning'}
                isLoading={isLoadingModels}
              />
              <StatusRow
                label="Models"
                value={isLoadingModels ? 'Loading' : models.length ? `${models.length} available` : 'Unavailable'}
                tone={models.length ? 'success' : 'warning'}
                isLoading={isLoadingModels}
              />
              <StatusRow label="Active model" value={activeModelName} tone={selectedModel ? 'success' : 'warning'} />
              <StatusRow label="Vitals tracked" value={`${selectedStatsCount} enabled`} tone="success" />
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
          <WorkflowCard icon={<MonitorHeartOutlined />} title="Monitor"
            body="Run a guided camera session and watch incoming heart, respiratory, pressure, and signal data."
            action="Open" onClick={() => navigate('/monitor')} />
          <WorkflowCard icon={<Tune />} title="Configure"
            body="Choose prediction models and decide which vitals should appear in monitoring sessions."
            action="Settings" onClick={() => setIsSettingsOpen(true)} />
          <WorkflowCard icon={<DescriptionOutlined />} title="Review"
            body="Create a structured summary of the latest readings for follow-up and documentation."
            action="Reports" onClick={() => navigate('/report')} />
          <WorkflowCard icon={<History />} title="History"
            body="Browse past monitoring sessions and track how your vitals change over time with trend charts."
            action="View history" onClick={() => navigate('/history')} />
          <WorkflowCard icon={<OndemandVideo />} title="Video Demo"
            body="Upload a pre-recorded video to run the full inference pipeline — ideal for demos and testing."
            action="Open demo" onClick={() => navigate('/demo')} />
        </Box>
      </Box>

      <Modal open={isSettingsOpen} onClose={() => setIsSettingsOpen(false)}>
        <Box sx={modalSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box>
              <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: '#0a1628' }}>Settings</Typography>
              <Typography sx={{ mt: 0.4, color: '#64748b', fontSize: '0.86rem' }}>Configure the monitoring experience.</Typography>
            </Box>
            <IconButton onClick={() => setIsSettingsOpen(false)} aria-label="Close settings"><Close /></IconButton>
          </Box>
          <Divider sx={{ my: 2.5 }} />
          <Typography sx={settingsLabelSx}>Prediction model</Typography>
          <FormControl fullWidth>
            <Select value={selectedModel} onChange={(event) => updateSelectedModel(event.target.value)}
              displayEmpty disabled={isLoadingModels || models.length === 0}
              sx={{ borderRadius: '12px', bgcolor: '#f8fafc' }}>
              {models.length === 0
                ? <MenuItem disabled value="">No models available</MenuItem>
                : models.map((model) => <MenuItem key={model.id} value={model.id}>{model.name}</MenuItem>)
              }
            </Select>
          </FormControl>
          <Typography sx={{ ...settingsLabelSx, mt: 3 }}>Vitals</Typography>
          <Typography sx={{ fontSize: '0.74rem', color: '#64748b', mt: -0.5, mb: 0.5 }}>
            Only showing what {activeModelName} actually measures.
          </Typography>
          <FormGroup sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.5 }}>
            {Object.keys(selectedStats)
              // temperature has no model dependency (no model measures it at
              // all yet — it's gated on camera hardware, not model choice),
              // so it's exempt from capability filtering.
              .filter((stat) => stat === 'temperature' || modelCapabilities.vitals[stat as keyof typeof modelCapabilities.vitals])
              .map((stat) => (
                <FormControlLabel key={stat}
                  control={<Checkbox checked={selectedStats[stat as keyof typeof selectedStats]}
                    onChange={() => toggleStat(stat as keyof typeof selectedStats)} />}
                  label={statLabels[stat as keyof typeof statLabels]} />
              ))}
          </FormGroup>

          {selectedModel.toLowerCase().includes('rhythmnet') && (
            <>
              <Divider sx={{ my: 2.5 }} />
              <Typography sx={settingsLabelSx}>RhythmNet streaming</Typography>
              <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8', mb: 1.5, mt: -0.5 }}>
                Advanced — see the <a href="/docs/rhythmnet-settings.md" target="_blank" rel="noreferrer">streaming settings guide</a> for what each one trades off.
              </Typography>

              <Typography sx={{ ...settingsLabelSx, fontSize: '0.78rem', mb: 0.5 }}>Connection mode</Typography>
              <FormControl fullWidth size="small">
                <Select value={rhythmNetTransport} onChange={(e) => updateRhythmNetTransport(e.target.value as 'ws' | 'http')}
                  sx={{ borderRadius: '12px', bgcolor: '#f8fafc' }}>
                  <MenuItem value="ws">Streaming (lowest latency)</MenuItem>
                  <MenuItem value="http">Classic (batched requests)</MenuItem>
                </Select>
              </FormControl>

              <Typography sx={{ ...settingsLabelSx, fontSize: '0.78rem', mb: 0.5, mt: 2 }}>Update frequency</Typography>
              <FormControl fullWidth size="small">
                <Select value={rhythmNetHopFrames} onChange={(e) => updateRhythmNetHopFrames(Number(e.target.value))}
                  sx={{ borderRadius: '12px', bgcolor: '#f8fafc' }}>
                  <MenuItem value={1}>Every frame (default — smoothest, matches the reference demo)</MenuItem>
                  <MenuItem value={10}>Fast (~0.3s, slightly less GPU load)</MenuItem>
                  <MenuItem value={40}>4x as often (~1.3s)</MenuItem>
                  <MenuItem value={80}>Twice as often (~2.7s, can look choppy)</MenuItem>
                  <MenuItem value={160}>Every full window (~5.3s, no overlap — choppiest)</MenuItem>
                </Select>
              </FormControl>

              <Typography sx={{ ...settingsLabelSx, fontSize: '0.78rem', mb: 0.5, mt: 2 }}>Initial buffer</Typography>
              <FormControl fullWidth size="small">
                <Select value={rhythmNetBufferWindows} onChange={(e) => updateRhythmNetBufferWindows(Number(e.target.value))}
                  sx={{ borderRadius: '12px', bgcolor: '#f8fafc' }}>
                  <MenuItem value={1}>Fastest first reading (~5.3s)</MenuItem>
                  <MenuItem value={2}>Steadier first reading (~11s)</MenuItem>
                  <MenuItem value={3}>Most stable first reading (~16s)</MenuItem>
                </Select>
              </FormControl>

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
                <Box sx={{ pr: 2 }}>
                  <Typography sx={{ ...settingsLabelSx, fontSize: '0.78rem' }}>Extra signal smoothing</Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                    Experimental — off shows the signals as RhythmNet itself produces them; on applies additional filtering that can over-smooth ECG/BP.
                  </Typography>
                </Box>
                <Switch checked={rhythmNetDenoiseEnabled}
                  onChange={e => updateRhythmNetDenoiseEnabled(e.target.checked)}
                  sx={{ '& .MuiSwitch-thumb': { bgcolor: rhythmNetDenoiseEnabled ? '#0a1628' : undefined } }} />
              </Box>
            </>
          )}

          <Divider sx={{ my: 2.5 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Box>
              <Typography sx={settingsLabelSx}>Daily scan reminder</Typography>
              <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                {isNative ? 'Get a daily nudge to complete your scan.' : 'Available in the mobile app.'}
              </Typography>
            </Box>
            <Switch checked={reminderEnabled} disabled={!isNative}
              onChange={e => handleReminderToggle(e.target.checked)}
              sx={{ '& .MuiSwitch-thumb': { bgcolor: reminderEnabled ? '#0a1628' : undefined } }} />
          </Box>
          {reminderEnabled && isNative && (
            <TextField label="Reminder time" type="time" value={reminderTime}
              onChange={e => handleReminderTimeChange(e.target.value)}
              inputProps={{ step: 300 }}
              sx={{ mt: 1, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} fullWidth />
          )}
          {reminderError && (
            <Alert severity="warning" sx={{ mt: 1, borderRadius: '10px', fontSize: '0.82rem' }}>{reminderError}</Alert>
          )}
          <Button fullWidth variant="contained" onClick={() => setIsSettingsOpen(false)} sx={{ ...primaryButtonSx, mt: 3 }}>
            Save preferences
          </Button>
        </Box>
      </Modal>
    </Box>
  );
};

const StatusRow: React.FC<{ label: string; value: string; tone: 'success' | 'warning'; isLoading?: boolean }> = ({
  label,
  value,
  tone,
  isLoading = false,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1, borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
    <Typography sx={{ color: 'rgba(255,255,255,0.48)', fontSize: '0.84rem' }}>{label}</Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {isLoading && <CircularProgress size={13} sx={{ color: 'rgba(255,255,255,0.5)' }} />}
      <Typography sx={{ color: tone === 'success' ? '#86efac' : '#fbbf24', fontWeight: 700, fontSize: '0.84rem', textAlign: 'right' }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

const WorkflowCard: React.FC<{ icon: React.ReactElement; title: string; body: string; action: string; onClick: () => void }> = ({
  icon,
  title,
  body,
  action,
  onClick,
}) => (
  <Box sx={workflowCardSx}>
    <Box sx={{ color: '#93c5fd', mb: 2 }}>{React.cloneElement(icon, { sx: { fontSize: 30 } })}</Box>
    <Typography sx={{ color: 'white', fontSize: '1.12rem', fontWeight: 800 }}>{title}</Typography>
    <Typography sx={{ color: 'rgba(255,255,255,0.48)', fontSize: '0.86rem', lineHeight: 1.65, mt: 1, minHeight: 68 }}>
      {body}
    </Typography>
    <Button onClick={onClick} sx={{ mt: 2, color: '#93c5fd', fontWeight: 800, textTransform: 'none', px: 0 }}>
      {action}
    </Button>
  </Box>
);

const statLabels = {
  heartRate: 'Heart rate',
  systolicBP: 'Systolic BP',
  diastolicBP: 'Diastolic BP',
  map: 'MAP',
  respiratoryRate: 'Respiratory rate',
  temperature: 'Temperature',
  spo2: 'SpO2',
  hrv: 'HRV',
};

const headerIconSx = {
  color: 'rgba(255,255,255,0.78)',
  bgcolor: 'rgba(255,255,255,0.08)',
  border: '1px solid rgba(255,255,255,0.1)',
  '&:hover': { bgcolor: 'rgba(255,255,255,0.14)' },
};

const panelSx = {
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '18px',
  bgcolor: 'rgba(10,28,60,0.58)',
  boxShadow: '0 16px 50px rgba(0,0,0,0.22)',
  p: { xs: 2.5, md: 3 },
};

const workflowCardSx = {
  ...panelSx,
  minHeight: 230,
};

const panelLabelSx = {
  color: 'rgba(255,255,255,0.42)',
  fontSize: '0.72rem',
  fontWeight: 800,
  letterSpacing: '2.5px',
  textTransform: 'uppercase',
};

const primaryButtonSx = {
  py: 1.3,
  px: 2.8,
  borderRadius: '12px',
  bgcolor: 'white',
  color: '#0a1628',
  fontWeight: 800,
  textTransform: 'none',
  boxShadow: '0 12px 28px rgba(255,255,255,0.12)',
  '&:hover': { bgcolor: '#e8f0fe' },
  '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.28)' },
};

const secondaryButtonSx = {
  py: 1.3,
  px: 2.4,
  borderRadius: '12px',
  color: 'rgba(255,255,255,0.82)',
  borderColor: 'rgba(255,255,255,0.16)',
  fontWeight: 800,
  textTransform: 'none',
  '&:hover': { borderColor: 'rgba(255,255,255,0.36)', bgcolor: 'rgba(255,255,255,0.06)' },
};

const modalSx = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: 'calc(100vw - 32px)', sm: 520 },
  maxHeight: 'calc(100vh - 48px)',
  overflowY: 'auto',
  bgcolor: 'white',
  borderRadius: '16px',
  p: { xs: 2.5, sm: 3 },
  boxShadow: '0 24px 80px rgba(0,0,0,0.32)',
};

const settingsLabelSx = {
  color: '#0a1628',
  fontSize: '0.84rem',
  fontWeight: 800,
  mb: 1,
};

export default HomePage;
